import { ChevronDown, Loader2, Send, Sparkles } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { parseError } from '@/lib/errors'
import { useCreateNewDraft } from '@/models/salesLead'
import { type LeadEmail } from './contactEmails'

interface Props {
  // Required only for the "New AI draft" action (lead-scoped).
  leadId?: string
  // Reachable recipient emails for this lead (primary first). May be empty.
  emails: LeadEmail[]
  // Called with the recipient to compose to (null when no email is on file).
  onCompose: (recipient: LeadEmail | null) => void
  // Offer "New AI draft" in the dropdown (default true). Carriers pass false —
  // AI drafting is lead-only.
  enableAiDraft?: boolean
}

function recipientItemLabel(email: LeadEmail): string {
  return email.label && email.label !== 'Email' ? `${email.label} · ${email.value}` : email.value
}

export function EmailButton({ leadId, emails, onCompose, enableAiDraft = true }: Props) {
  const createNewDraft = useCreateNewDraft()
  // AI drafting is lead-only, so it needs a leadId; carriers pass enableAiDraft=false.
  const showDraftItem = enableAiDraft && !!leadId
  const isDrafting = createNewDraft.isPending
  const primary = emails[0] ?? null
  const hasMultipleEmails = emails.length > 1

  const onNewAiDraft = () => {
    if (!leadId) return
    createNewDraft.mutate(
      { leadId },
      {
        onSuccess: () => toast.success('AI draft added to the activity feed'),
        onError: (err) => toast.error(parseError(err)),
      }
    )
  }

  const composeBtnClass = 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800'

  // No dropdown needed: at most one recipient and nothing else to offer.
  if (!hasMultipleEmails && !showDraftItem) {
    return (
      <Button variant="outline" className={composeBtnClass} onClick={() => onCompose(primary)}>
        <Send className="mr-1 size-4" />
        Email
      </Button>
    )
  }

  return (
    <div className="inline-flex">
      <Button
        variant="outline"
        className={`rounded-r-none border-r-0 ${composeBtnClass}`}
        onClick={() => onCompose(primary)}
      >
        <Send className="mr-1 size-4" />
        Email
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            aria-label="More email options"
            className={`rounded-l-none px-2 ${composeBtnClass}`}
            disabled={isDrafting}
          >
            {isDrafting ? <Loader2 className="size-4 animate-spin" /> : <ChevronDown className="size-4" />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {hasMultipleEmails && (
            <>
              <DropdownMenuLabel>Send to</DropdownMenuLabel>
              {emails.map((email) => (
                <DropdownMenuItem key={email.value} onSelect={() => onCompose(email)}>
                  {recipientItemLabel(email)}
                </DropdownMenuItem>
              ))}
            </>
          )}
          {hasMultipleEmails && showDraftItem && <DropdownMenuSeparator />}
          {showDraftItem && (
            <DropdownMenuItem onSelect={onNewAiDraft} disabled={isDrafting}>
              <Sparkles className="mr-2 size-4" />
              New AI draft
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
